import { PerStyleVariationMapBuilder } from "@faire/deprecated-retailer-visitor-shared/features/OpenSizing/PerStyleVariationMapBuilder";
import { isProductOptionInStock } from "@faire/deprecated-retailer-visitor-shared/lib/productOptions";
import { showProductPromotions } from "@faire/deprecated-retailer-visitor-shared/lib/showProductPromotions";
import { getProductOptions } from "@faire/deprecated-retailer-visitor-shared/views/product/utils/productUtils";
import { Decorative } from "@faire/design-tokens";
import { Core } from "@faire/slate/foundation/color";
import { IProductOption } from "@faire/web-api--source/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import { formatMoney } from "@faire/web/common/money";
import Checkbox from "@faire/web/slate-deprecated/Checkbox";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Span, Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { type JSX } from "react";
import styled from "styled-components";

interface IProps {
  product: IProductPageResponse.IPdpProduct;
  includesTesters: boolean;
  selectedOption: IProductOption | undefined;
  perStyleOptionVariations:
    | PerStyleVariationMapBuilder.IPerStyleOptionVariation[]
    | undefined;
  onTesterCheckboxChange: () => void;
}

export const TesterCheckbox: React.FC<IProps> = ({
  product,
  includesTesters,
  selectedOption,
  perStyleOptionVariations,
  onTesterCheckboxChange,
}) => {
  const hasProductOptionWithTesters = getProductOptions(product).some(
    (option) => option.has_testers
  );

  if (
    !product.purchase_information?.tester_price ||
    !hasProductOptionWithTesters
  ) {
    return null;
  }

  if (
    !selectedOption ||
    !isProductOptionInStock(selectedOption, perStyleOptionVariations) ||
    !selectedOption.has_testers
  ) {
    return <Spacer size={20} />;
  }

  return (
    <>
      <Checkbox checked={includesTesters} onChange={onTesterCheckboxChange}>
        <Typography data-test-id="add-to-cart-include-tester">
          {showProductPromotions(
            product.purchase_information.tester_promo_price
          ) ? (
            <StrictLocalMsg
              id="0SNwnz"
              defaultMessage="Include a Tester for <StrikeThroughFormatting>{originalTesterMoney}</StrikeThroughFormatting> <RustFormatting>{discountedTesterMoney}</RustFormatting>"
              description={{
                text: "Click the checkbox to add a discounted tester product",
              }}
              values={{
                originalTesterMoney: formatMoney({
                  money: product.purchase_information.tester_price,
                }),
                discountedTesterMoney: formatMoney({
                  money:
                    product.purchase_information.tester_promo_price
                      .discounted_item_price,
                }),
                StrikeThroughFormatting: strikeThroughFormatting,
                RustFormatting: rustFormatting,
              }}
            />
          ) : (
            <StrictLocalMsg
              id="bTJMhn"
              defaultMessage="Include a Tester for {testerMoney}"
              description={{
                text: "Click the checkbox to add a tester product",
              }}
              values={{
                testerMoney: formatMoney({
                  money:
                    product.purchase_information.tester_promo_price
                      ?.discounted_item_price ??
                    product.purchase_information.tester_price,
                }),
              }}
            />
          )}
        </Typography>
      </Checkbox>
      <Row>
        <Spacer size="3x" />
        <Typography color={Core.text.subdued} variant="labelSansRegular">
          <StrictLocalMsg
            id="Ke2+qw"
            defaultMessage="Tester items are not returnable"
            description={{
              text: "We will not accept items that are testers for return",
            }}
          />
        </Typography>
      </Row>
      <Spacer size="2x" />
    </>
  );
};

const strikeThroughFormatting = (chunk: JSX.Element) => (
  <StrikeThrough>{chunk}</StrikeThrough>
);

const rustFormatting = (chunk: JSX.Element) => (
  <Span color={Decorative.shade.orange}> {chunk} </Span>
);

const StrikeThrough = styled.span`
  text-decoration: line-through;
  color: ${Core.text.subdued};
`;
